import { useState } from 'react';
import { IdType } from 'types/enums';
import { Ipi } from 'types/ipi.types';
import { Ipn } from 'types/ipn.types';
import { Isni } from 'types/isni.types';
import { useDisclosure, UseDisclosureReturn } from '@chakra-ui/react';
import {
  deleteIpiEffect,
  deleteIpnEffect,
  deleteIsniEffect,
} from 'app/store/Profile/effects';
import {
  IdProps,
  SocietyOption,
  V3Claim,
} from 'app/components/Profile/Ids/types';
import { AppDispatch } from 'app/store';

interface UseIdsListStateProps {
  ipiProps: IdProps<Ipi>;
  ipnProps: IdProps<Ipn>;
  isniProps: IdProps<Isni>;
  addId: IdType | null;
  setAddId: (state: IdType | null) => void;
  v3Claim?: V3Claim;
  requestGuidForVerification?: string;
  handleIdDialogClose: () => void;
  updateV3ClaimVerificationData: (data: SocietyOption) => void;
  emailConfirmDisclosure: UseDisclosureReturn;
  verificationDisclosure: UseDisclosureReturn;
  onConfirmClaimDialogClose: () => void;
  onEmailClaimDialogClose: (
    needConfirm?: boolean,
    requestGuid?: string
  ) => void;
}

export const useIdsListState: (
  dispatch: AppDispatch
) => UseIdsListStateProps = (dispatch) => {
  const [addId, setAddId] = useState<IdType | null>(null);
  const [ipiToDelete, setIpiToDelete] = useState<Ipi>();
  const [ipiToUpdate, setIpiToUpdate] = useState<Ipi>();

  const [ipnToUpdate, setIpnToUpdate] = useState<Ipn>();
  const [ipnToDelete, setIpnToDelete] = useState<Ipn>();

  const [isniToUpdate, setIsniToUpdate] = useState<Isni>();
  const [isniToDelete, setIsniToDelete] = useState<Isni>();

  const [v3Claim, setV3Claim] = useState<V3Claim>();
  const [
    requestGuidForVerification,
    setRequestGuidForVerification,
  ] = useState<string>();

  const emailConfirmDisclosure = useDisclosure();
  const verificationDisclosure = useDisclosure();

  const handleVerificationDialogsClose = () => {
    setV3Claim(undefined);
    setRequestGuidForVerification(undefined);
  };

  const handleIdDialogClose = () => {
    setAddId(null);
    setIpnToUpdate(undefined);
    setIpiToUpdate(undefined);
    setIsniToUpdate(undefined);
  };

  const handleDeleteIpi = async () => {
    if (ipiToDelete) {
      try {
        await dispatch(
          deleteIpiEffect({
            ipiNameNumber: ipiToDelete.ipiNameNumber,
            link: ipiToDelete._links?.deleteIpi,
          })
        ).unwrap();
        setIpiToDelete(undefined);
      } catch (e) {
        // handle error
      }
    }
  };

  const handleDeleteIpn = async () => {
    if (ipnToDelete) {
      try {
        await dispatch(
          deleteIpnEffect({
            ipnNumber: ipnToDelete.ipnNumber,
            link: ipnToDelete._links.deleteIpn,
          })
        ).unwrap();
        setIpnToDelete(undefined);
      } catch (e) {
        // handle error
      }
    }
  };

  const handleDeleteIsni = async () => {
    if (isniToDelete) {
      try {
        await dispatch(
          deleteIsniEffect({
            isniNumber: isniToDelete.isni,
            link: isniToDelete._links?.deleteIsni,
          })
        ).unwrap();
        setIsniToDelete(undefined);
      } catch (e) {
        // handle error
      }
    }
  };

  const updateV3ClaimVerificationData = (data: SocietyOption): void => {
    setV3Claim({ id: data.id, societyName: data.title });
    emailConfirmDisclosure.onOpen();
    handleIdDialogClose();
  };

  const onEmailClaimDialogClose = (
    needConfirm?: boolean,
    requestGuid?: string
  ) => {
    emailConfirmDisclosure.onClose();
    if (needConfirm && requestGuid) {
      setRequestGuidForVerification(requestGuid);
      verificationDisclosure.onOpen();
    }
  };

  const onConfirmClaimDialogClose = () => {
    handleVerificationDialogsClose();
    verificationDisclosure.onClose();
  };

  return {
    addId,
    setAddId,
    v3Claim,
    requestGuidForVerification,
    handleIdDialogClose,
    updateV3ClaimVerificationData,
    emailConfirmDisclosure,
    verificationDisclosure,
    onEmailClaimDialogClose,
    onConfirmClaimDialogClose,
    ipiProps: {
      idToUpdate: ipiToUpdate,
      idToDelete: ipiToDelete,
      setIdToUpdate: setIpiToUpdate,
      setIdToDelete: setIpiToDelete,
      handleDeleteId: handleDeleteIpi,
    },
    ipnProps: {
      idToUpdate: ipnToUpdate,
      idToDelete: ipnToDelete,
      setIdToUpdate: setIpnToUpdate,
      setIdToDelete: setIpnToDelete,
      handleDeleteId: handleDeleteIpn,
    },
    isniProps: {
      idToUpdate: isniToUpdate,
      idToDelete: isniToDelete,
      setIdToUpdate: setIsniToUpdate,
      setIdToDelete: setIsniToDelete,
      handleDeleteId: handleDeleteIsni,
    },
  };
};
